import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { authService } from '../services/auth.service';
import { useAuth } from '../contexts/AuthContext';
import { GoogleProfile } from '../services/auth.service';
import './Register.css';

interface LocationState {
  googleProfile?: GoogleProfile;
  idToken?: string;
}

const Register: React.FC = () => {
  const location = useLocation();
  const state = (location.state as LocationState) || {};
  const googleProfile = state.googleProfile;
  const idToken = state.idToken;

  const [formData, setFormData] = useState({
    name: googleProfile?.name || '',
    company: '',
    department: '',
    email: googleProfile?.email || '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { setAuthFromResponse } = useAuth();

  // Google OAuth를 통해 온 경우가 아니면 로그인으로 리다이렉트
  if (!googleProfile || !idToken) {
    return (
      <div className="register-container">
        <div className="register-card">
          <h2>회원가입</h2>
          <p>Google 로그인을 먼저 진행해주세요.</p>
          <div className="login-link">
            <a href="/login">로그인 페이지로 이동</a>
          </div>
        </div>
      </div>
    );
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!formData.name.trim()) {
      setError('이름을 입력해주세요.');
      return;
    }
    if (!formData.company.trim()) {
      setError('회사명을 입력해주세요.');
      return;
    }
    if (!formData.department.trim()) {
      setError('부서명을 입력해주세요.');
      return;
    }

    setIsLoading(true);

    try {
      await authService.registerGoogle({
        idToken,
        name: formData.name,
        company: formData.company,
        department: formData.department,
      });

      // 가입 후 바로 로그인
      const loginResult = await authService.googleLogin(idToken);
      if (loginResult.access_token && loginResult.user) {
        await setAuthFromResponse({
          access_token: loginResult.access_token,
        });
        navigate('/dashboard');
      } else {
        navigate('/login');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || '회원가입에 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="register-container">
      <div className="register-card">
        <h2>추가 정보 입력</h2>
        <p className="register-description">
          Google 계정 정보가 확인되었습니다. 추가 정보를 입력해주세요.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="email">이메일</label>
            <input
              id="email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              readOnly
              className="readonly-input"
            />
          </div>
          <div className="form-group">
            <label htmlFor="name">이름</label>
            <input
              id="name"
              name="name"
              type="text"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="company">회사명</label>
            <input
              id="company"
              name="company"
              type="text"
              value={formData.company}
              onChange={handleChange}
              required
              placeholder="예: 세아그룹, VNTG"
            />
          </div>
          <div className="form-group">
            <label htmlFor="department">부서명</label>
            <input
              id="department"
              name="department"
              type="text"
              value={formData.department}
              onChange={handleChange}
              required
              placeholder="예: 정보보호팀"
            />
          </div>
          {error && <div className="error-message">{error}</div>}
          <button type="submit" disabled={isLoading} className="submit-button">
            {isLoading ? '가입 중...' : '가입 완료'}
          </button>
        </form>
        <div className="login-link">
          <a href="/login">로그인 페이지로 돌아가기</a>
        </div>
      </div>
    </div>
  );
};

export default Register;
